import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authSelectors } from 'redux/auth';
import { workspaceActions, workspaceSelectors, workspaceTypes } from 'redux/workspace';
import useReduxLoading from 'hooks/useReduxLoading';
import { isMobile } from 'helpers';

// Only for a layout that failed to load. Mirrors
// modules/Workspace/Home/Layouts/DefaultHomeLayout.php, which is the source of truth.
const mobileFallbackWidgets = [
  { type: 'greeting', config: { showAvatar: true } },
  { type: 'calendar', config: { size: 'small' } },
  { type: 'boosts' },
  { type: 'birthdays_and_job_anniversaries' },
  { type: 'feed' },
];

const webFallbackWidgets = [
  { type: 'greeting', config: { showAvatar: false } },
  { type: 'boosts' },
  { type: 'active_challenges' },
  { type: 'calendar', config: { size: 'large' } },
  { type: 'birthdays_and_job_anniversaries' },
  { type: 'feed' },
];

const fallbackWidgets = isMobile ? mobileFallbackWidgets : webFallbackWidgets;

// The backend puts the workspace's custom greeting in the greeting's config; the fallback takes it from auth/user
const withCustomGreeting = (widgets, customGreeting) => (customGreeting
  ? widgets.map(widget => (widget.type === 'greeting' ? { ...widget, config: { ...widget.config, subtitle: customGreeting } } : widget))
  : widgets);

// The app download prompt is the client's own: it shows itself only where this device registered or joined
export const withAppDownload = (widgets, mobile) => {
  if (mobile) {
    return widgets;
  }

  const at = widgets.findIndex(({ type }) => type === 'greeting') + 1;

  return [...widgets.slice(0, at), { id: 'app_download', type: 'app_download' }, ...widgets.slice(at)];
};

const useHomeLayout = () => {

  const dispatch = useDispatch();
  const workspaceId = useSelector(authSelectors.getWorkspaceId);

  const getHomeWidgets = useSelector(workspaceSelectors.getHomeWidgetsSelector);
  const widgets = getHomeWidgets(workspaceId);

  // The layout follows the member's permissions and features, and the greeting widget the workspace's custom greeting,
  // so a change to either loads it again
  const memberKey = useSelector(authSelectors.getMemberKey);
  const customGreeting = useSelector(authSelectors.getWorkspace)?.customGreeting;
  const layoutKey = JSON.stringify([memberKey, customGreeting ?? null]);
  const loadedForLayoutKey = useSelector(workspaceSelectors.getHomeLayoutKey);

  useEffect(() => {
    if (!widgets || loadedForLayoutKey !== layoutKey) {
      dispatch(workspaceActions.getHome({ workspaceId, layoutKey }));
    }
  }, [dispatch, widgets, workspaceId, layoutKey, loadedForLayoutKey]);

  const requesting = useReduxLoading(
    [workspaceTypes.getHome],
    [workspaceTypes.getHomeSuccess, workspaceTypes.getHomeFailure],
    true,
    workspaceId,
  );

  return useMemo(() => ({
    widgets: withAppDownload(widgets ?? withCustomGreeting(fallbackWidgets, customGreeting), isMobile),
    loading: !widgets && requesting,
  }), [widgets, requesting, customGreeting]);
};

export default useHomeLayout;
